import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Plus,
  Edit3,
  Trash2,
  Settings,
  PieChart,
  FolderOpen,
  Building2,
  Globe,
  Save,
  X,
  Database,
  Upload,
  Sliders,
  Eye,
  EyeOff,
  Lock,
  Bot,
  CreditCard,
  Tag,
  Mail,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  AtSign,
  Landmark,
} from 'lucide-react'
import { usePortfolioStore, type Sector, type Country } from '../store/portfolioStore'
import { useBrokerStore, type Broker } from '../store/brokerStore'
import BrokerLogo from './BrokerLogo'
import { useConfigStore } from '../store/configStore'
import { apiFetch } from '../lib/api'
import Button, { buttonClass } from './ui/Button'
import Badge from './ui/Badge'
import Select from './ui/Select'
import toast from 'react-hot-toast'
import BotSettingsSection from './BotSettingsSection'
import { pageContainerVariants, pageItemVariants } from '../constants/motionVariants'

const PortfolioManagement: React.FC = () => {
  const {
    customPortfolios,
    selectedPortfolioId,
    openCustomPortfolioModal,
    deleteCustomPortfolio,
    sectors,
    loadSectors,
    createSector,
    updateSector,
    deleteSector,
    countries,
    loadCountries,
    createCountry,
    updateCountry,
    deleteCountry,
    clearDatabase,
    openCsvImportModal
  } = usePortfolioStore()

  const {
    brokers,
    loadBrokers,
    createBroker,
    updateBroker,
    deleteBroker,
  } = useBrokerStore()

  const {
    getConfigValue,
    updateConfig,
    fetchConfig,
  } = useConfigStore()

  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null)
  const [isAddSectorModalOpen, setIsAddSectorModalOpen] = useState(false)
  const [editingSectorId, setEditingSectorId] = useState<number | null>(null)
  const [isAddCountryModalOpen, setIsAddCountryModalOpen] = useState(false)
  const [editingCountryId, setEditingCountryId] = useState<number | null>(null)
  const [isAddBrokerModalOpen, setIsAddBrokerModalOpen] = useState(false)
  const [editingBrokerId, setEditingBrokerId] = useState<number | null>(null)
  const [activeSection, setActiveSection] = useState<'portfolios' | 'sectors' | 'countries' | 'brokers' | 'configuration' | 'bot' | 'credit'>('portfolios')
  const [showClearConfirm, setShowClearConfirm] = useState(false)
  const [sectorFormData, setSectorFormData] = useState({
    key: '',
    label: '',
    icon: '',
    color: 'blue',
    bg_color: 'bg-blue-50 dark:bg-blue-900/20',
    text_color: 'text-blue-700 dark:text-blue-300',
    border_color: 'border-blue-200 dark:border-blue-700'
  })
  const [countryFormData, setCountryFormData] = useState({
    key: '',
    label: '',
    icon: '',
    color: 'blue',
    bg_color: 'bg-blue-50 dark:bg-blue-900/20',
    text_color: 'text-blue-700 dark:text-blue-300',
    border_color: 'border-blue-200 dark:border-blue-700'
  })
  const [brokerFormData, setBrokerFormData] = useState({
    key: '',
    label: '',
    icon: '',
    color: 'blue',
    bg_color: 'bg-blue-50 dark:bg-blue-900/20',
    text_color: 'text-blue-700 dark:text-blue-300',
    border_color: 'border-blue-200 dark:border-blue-700'
  })

  const [googleSheetsUrl, setGoogleSheetsUrl] = useState(
    getConfigValue('google_sheets_url', 'https://docs.google.com/spreadsheets/d/1NH5JOSkN5fbnbUwaUEaAN80Qcfaki03NpiT8R6EOjj0')
  )
  const [shayPortfolioUrl, setShayPortfolioUrl] = useState(
    getConfigValue('shay_portfolio_url', 'https://docs.google.com/spreadsheets/d/1vCkxvFGKYDeLag5k2WSIjSFR0Zl2nuUi9cye6leOf5g')
  )
  const [isSavingConfig, setIsSavingConfig] = useState(false)
  const [pdfPassword, setPdfPassword] = useState(getConfigValue('pdf_password', ''))
  const [showPdfPassword, setShowPdfPassword] = useState(false)
  const [isSavingPdfPassword, setIsSavingPdfPassword] = useState(false)

  const DEFAULT_CREDIT_KEYWORDS: Record<string, string[]> = {
    'ชอปปิ้ง': ['LAZADA','SHOPEE','CENTRAL','ROBINSON','BIG C','LOTUS','MAKRO','IKEA','ZEN','HOMEPRO','POWERBUY','JAYMART','ADVICE','GLOBAL HOUSE','UNIQLO','H&M','ZARA','SUPERSPORTS','(FOR SHOPEE)','WWW.2C2P.COM*LAZADA'],
    'ร้านอาหาร': ['RESTAURANT','CAFE','COFFEE','MK ','OISHI','SIZZLER','MCDONALD','KFC','PIZZA','BURGER','GRAB FOOD','WWW.GRAB.COM','LINE MAN WO','FOOD PANDA','AFTER YOU','SWENSEN','STARBUCKS','AMAZON CAFE','TRUE COFFEE','DAIRY QUEEN','LINEPAY*PF_LINE MAN','LPTH*PF_LM_','TONCHIN','SZ-414'],
    'สุขภาพ': ['HOSPITAL','CLINIC','PHARMACY','DRUG STORE','BUMRUNGRAD','SAMITIVEJ','BANGKOK DUSIT MED','PHYATHAI','MEDPARK','BOOTS','WATSONS','GUARDIAN'],
    'เดินทาง': ['THAI AIR','AIRASIA','NOKAIR','LION AIR','SCOOT','JETSTAR','BOLT','LINEPAY*LP_BTS','BSS*BTS','BEM','MRT','AIRPORT','TAXI','HOTEL','AGODA','BOOKING.COM','AIRBNB','EXPEDIA'],
    'น้ำมัน': ['PTTST.','PTT STATION','SHELL','CALTEX','ESSO','BANGCHAK','SUSCO','OR STATION','BSRC-DDK POWER','BK-'],
    'บันเทิง': ['CINEMA','MAJOR CINEPLEX','SF CINEMA','STEAM','PLAYSTATION','NINTENDO','JOOX'],
    'ค่า Subscription': ['NETFLIX','SPOTIFY','YOUTUBE','APPLE.COM','GOOGLE PLAY','GOOGLE ONE','MICROSOFT 365','MICROSOFT OFFICE','ADOBE','CANVA','DROPBOX','NOTION','CLAUDE.AI','CHATGPT','OPENAI','LINE STICKER','AMAZON PRIME','DISNEY+'],
    'e-Commerce': ['AMAZON','EBAY','ALIEXPRESS','JD.COM','PAYPAL','SHOPIFY','TIKTOK SHOP'],
    'ซุปเปอร์มาเก็ต': ['7-ELEVEN','7ELEVEN','7-11','TMN 7-11','FAMILY MART','FAMILYMART','LAWSON','MINI STOP','TOPS','GOURMET MARKET','FOODLAND','MAX VALU','MAXVALU','RIMPING','TESCO','VILLA MARKET'],
    'ค่าน้ำ': ['MWA','PWA','MEA WATER','PROVINCIAL WATERWORKS','METROPOLITAN WATERWORKS'],
    'ค่าไฟ': ['MEA','PEA','METROPOLITAN ELECTRICITY','PROVINCIAL ELECTRICITY','EGAT'],
    'ค่าเน็ต': ['TRUE ONLINE','AIS FIBRE','AIS FIBER','NT INTERNET','CAT TELECOM','3BB','NTTELECOM','TOT INTERNET','TRUE MOVE H'],
  }
  const [creditKeywords, setCreditKeywords] = useState<Record<string, string[]>>(DEFAULT_CREDIT_KEYWORDS)
  const [newKwInputs, setNewKwInputs] = useState<Record<string, string>>({})
  const [isSavingCreditKws, setIsSavingCreditKws] = useState(false)
  const [isCreditKwsLoaded, setIsCreditKwsLoaded] = useState(false)

  type CardProfile = { name: string; image: string | null }
  const AVAILABLE_CARD_IMAGES = [
    { path: '/creditcards/aeon-next-gen.webp', label: 'AEON Next Gen' },
    { path: '/creditcards/aeon_mgen.webp', label: 'AEON mGen', portrait: true },
    { path: '/creditcards/krungsri-first-choice-visa-platinum-card.webp', label: 'Krungsri First Choice', portrait: true },
    { path: '/creditcards/krungsri-jcb-platinum.webp', label: 'Krungsri JCB Platinum' },
    { path: '/creditcards/ktc-jcb-ultimate.webp', label: 'KTC JCB Ultimate' },
    { path: '/creditcards/ktc-unionpay-diamond.webp', label: 'KTC UnionPay Diamond' },
    { path: '/creditcards/ktc-visa-platinum.webp', label: 'KTC Visa Platinum' },
    { path: '/creditcards/ktc-visa-signature.webp', label: 'KTC Visa Signature' },
    { path: '/creditcards/ttb-absolute.webp', label: 'TTB Absolute' },
    { path: '/creditcards/ttb-so-fast.webp', label: 'TTB So Fast' },
    { path: '/creditcards/ttb-so-smart.webp', label: 'TTB So Smart' },
    { path: '/creditcards/uob-one-vs.webp', label: 'UOB One', portrait: true },
    { path: '/creditcards/uob-premier.webp', label: 'UOB Premier' },
    { path: '/creditcards/uob-privi-miles.webp', label: 'UOB Privi Miles' },
    { path: '/creditcards/uob-simple-vs.webp', label: 'UOB Simple VS', portrait: true },
    { path: '/creditcards/uob-world-mc.webp', label: 'UOB World' },
  ]
  type UniqueCard = { last4: string; bank: string; cardType: string }
  const [cardProfiles, setCardProfiles] = useState<Record<string, CardProfile>>({})
  const [uniqueCards, setUniqueCards] = useState<UniqueCard[]>([])
  const [isCardProfilesLoaded, setIsCardProfilesLoaded] = useState(false)
  const [isSavingCardProfiles, setIsSavingCardProfiles] = useState(false)
  const PRESET_SENDERS: { pattern: string; label: string }[] = [
    { pattern: 'from:statement@krungsri.com', label: 'Krungsri' },
    { pattern: 'from:e_statement@cardx.co.th', label: 'CardX' },
    { pattern: 'from:GSB-Estatement@gsb.or.th', label: 'GSB' },
    { pattern: 'from:ClickKTC@ktc.co.th', label: 'KTC' },
    { pattern: 'from:estatement@eservice.ttbbank.com', label: 'TTB' },
    { pattern: 'from:estatement@esm.aeon.co.th', label: 'AEON' },
    { pattern: 'from:K-eMailStatement@kasikornbank.com', label: 'Kasikorn' },
    { pattern: 'from:BBL_eStatement@bangkokbank.com', label: 'Bangkok Bank' },
  ]

  const [creditTab, setCreditTab] = useState<'profiles' | 'categories' | 'gmail'>('profiles')
  const [gmailStatus, setGmailStatus] = useState<{ connected: boolean; email: string; last_sync: string | null } | null>(null)
  const [gmailSyncing, setGmailSyncing] = useState(false)
  const [gmailSyncResult, setGmailSyncResult] = useState<{ found: number; saved: number; skipped: number; errors: string[] } | null>(null)
  const [gmailSenders, setGmailSenders] = useState<string[]>([])
  const [gmailSendersLoaded, setGmailSendersLoaded] = useState(false)
  const [newSenderInput, setNewSenderInput] = useState('')
  const [savingSenders, setSavingSenders] = useState(false)
  const [gmailPdfPasswords, setGmailPdfPasswords] = useState<string[]>([])
  const [gmailPdfPasswordsLoaded, setGmailPdfPasswordsLoaded] = useState(false)
  const [newPasswordInput, setNewPasswordInput] = useState('')
  const [savingPasswords, setSavingPasswords] = useState(false)
  const [showPasswordValues, setShowPasswordValues] = useState(false)
  const [openPickerFor, setOpenPickerFor] = useState<string | null>(null)
  const [newCatName, setNewCatName] = useState('')
  const [showAddCat, setShowAddCat] = useState(false)

  const colorOptions = [
    { name: 'Blue', value: 'blue', dot: 'bg-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-200 dark:border-blue-700' },
    { name: 'Red', value: 'red', dot: 'bg-red-500', bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-300', border: 'border-red-200 dark:border-red-700' },
    { name: 'Green', value: 'green', dot: 'bg-green-500', bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-300', border: 'border-green-200 dark:border-green-700' },
    { name: 'Yellow', value: 'yellow', dot: 'bg-yellow-400', bg: 'bg-yellow-50 dark:bg-yellow-900/20', text: 'text-yellow-700 dark:text-yellow-300', border: 'border-yellow-200 dark:border-yellow-700' },
    { name: 'Purple', value: 'purple', dot: 'bg-purple-500', bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-200 dark:border-purple-700' },
    { name: 'Orange', value: 'orange', dot: 'bg-orange-500', bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-200 dark:border-orange-700' },
    { name: 'Indigo', value: 'indigo', dot: 'bg-indigo-500', bg: 'bg-indigo-50 dark:bg-indigo-900/20', text: 'text-indigo-700 dark:text-indigo-300', border: 'border-indigo-200 dark:border-indigo-700' },
    { name: 'Gray', value: 'gray', dot: 'bg-gray-400', bg: 'bg-gray-50 dark:bg-gray-900/20', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-200 dark:border-gray-700' },
    { name: 'Cyan', value: 'cyan', dot: 'bg-cyan-500', bg: 'bg-cyan-50 dark:bg-cyan-900/20', text: 'text-cyan-700 dark:text-cyan-300', border: 'border-cyan-200 dark:border-cyan-700' },
    { name: 'Emerald', value: 'emerald', dot: 'bg-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-200 dark:border-emerald-700' },
    { name: 'Slate', value: 'slate', dot: 'bg-slate-500', bg: 'bg-slate-50 dark:bg-slate-900/20', text: 'text-slate-700 dark:text-slate-300', border: 'border-slate-200 dark:border-slate-700' },
  ]

  useEffect(() => {
    loadSectors()
    loadCountries()
    loadBrokers()
    // Handle Gmail OAuth redirect callback
    const params = new URLSearchParams(window.location.search)
    const gmailResult = params.get('gmail')
    if (gmailResult === 'connected') {
      const email = params.get('email') || ''
      setActiveSection('credit')
      setCreditTab('gmail')
      setGmailStatus({ connected: true, email, last_sync: null })
      toast.success('เชื่อมต่อ Gmail สำเร็จ')
      window.history.replaceState({}, '', window.location.pathname)
    } else if (gmailResult === 'error') {
      const detail = params.get('detail') || ''
      setActiveSection('credit')
      setCreditTab('gmail')
      toast.error('เชื่อมต่อ Gmail ไม่สำเร็จ: ' + detail)
      window.history.replaceState({}, '', window.location.pathname)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only load once on mount

  useEffect(() => {
    if (activeSection !== 'credit') return
    if (!isCreditKwsLoaded) {
      fetchConfig('credit_category_keywords').then(() => {
        const saved = getConfigValue('credit_category_keywords', '')
        if (saved) {
          try { setCreditKeywords(JSON.parse(saved)) } catch (_) {}
        }
        setIsCreditKwsLoaded(true)
      })
    }
    if (!isCardProfilesLoaded) {
      fetchConfig('credit_card_profiles').then(() => {
        const saved = getConfigValue('credit_card_profiles', '')
        if (saved) {
          try { setCardProfiles(JSON.parse(saved)) } catch (_) {}
        }
      })
      apiFetch('/api/credit-statements?months=60').then(r => r.json()).then(data => {
        const seen = new Set<string>()
        const cards: UniqueCard[] = []
        for (const r of (data.data || data || [])) {
          if (r.card_last4 && !seen.has(r.card_last4)) {
            seen.add(r.card_last4)
            cards.push({ last4: r.card_last4, bank: r.bank || '', cardType: r.card_type || '' })
          }
        }
        setUniqueCards(cards)
        setIsCardProfilesLoaded(true)
      }).catch(() => setIsCardProfilesLoaded(true))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection])

  // Load Gmail status + senders when Gmail tab opens
  useEffect(() => {
    if (activeSection !== 'credit' || creditTab !== 'gmail') return
    apiFetch('/api/gmail/status')
      .then(r => r.ok ? r.json() : null)
      .then(data => setGmailStatus(data))
      .catch(() => setGmailStatus({ connected: false, email: '', last_sync: null }))
    if (!gmailSendersLoaded) {
      apiFetch('/api/gmail/senders')
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data?.senders) { setGmailSenders(data.senders); setGmailSendersLoaded(true) } })
        .catch(() => {})
    }
    if (!gmailPdfPasswordsLoaded) {
      apiFetch('/api/gmail/passwords')
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) { setGmailPdfPasswords(data.passwords ?? []); setGmailPdfPasswordsLoaded(true) } })
        .catch(() => {})
    }
  }, [activeSection, creditTab])

  const saveSenders = async (list: string[]) => {
    setSavingSenders(true)
    try {
      await apiFetch('/api/gmail/senders', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ senders: list }),
      })
      setGmailSenders(list)
      toast.success('บันทึก sender เรียบร้อย')
    } catch { toast.error('บันทึกไม่สำเร็จ') }
    finally { setSavingSenders(false) }
  }

  const toggleSender = (pattern: string) => {
    const next = gmailSenders.includes(pattern)
      ? gmailSenders.filter(s => s !== pattern)
      : [...gmailSenders, pattern]
    saveSenders(next)
  }

  const addCustomSender = () => {
    const trimmed = newSenderInput.trim()
    if (!trimmed) return
    const pattern = trimmed.startsWith('from:') ? trimmed : `from:${trimmed}`
    if (gmailSenders.includes(pattern)) { toast('Sender นี้มีอยู่แล้ว', { icon: 'ℹ️' }); return }
    const next = [...gmailSenders, pattern]
    saveSenders(next)
    setNewSenderInput('')
  }

  const removeSender = (pattern: string) => {
    saveSenders(gmailSenders.filter(s => s !== pattern))
  }

  const savePdfPasswords = async (list: string[]) => {
    setSavingPasswords(true)
    try {
      await apiFetch('/api/gmail/passwords', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passwords: list }),
      })
      setGmailPdfPasswords(list)
      toast.success('บันทึก password เรียบร้อย')
    } catch { toast.error('บันทึกไม่สำเร็จ') }
    finally { setSavingPasswords(false) }
  }

  const addPdfPassword = () => {
    const trimmed = newPasswordInput.trim()
    if (!trimmed) return
    if (gmailPdfPasswords.includes(trimmed)) { toast('Password นี้มีอยู่แล้ว', { icon: 'ℹ️' }); return }
    savePdfPasswords([...gmailPdfPasswords, trimmed])
    setNewPasswordInput('')
  }

  // Update config values when configs load
  useEffect(() => {
    const alphaUrl = getConfigValue('google_sheets_url', 'https://docs.google.com/spreadsheets/d/1NH5JOSkN5fbnbUwaUEaAN80Qcfaki03NpiT8R6EOjj0')
    const shayUrl = getConfigValue('shay_portfolio_url', 'https://docs.google.com/spreadsheets/d/1vCkxvFGKYDeLag5k2WSIjSFR0Zl2nuUi9cye6leOf5g')
    setGoogleSheetsUrl(alphaUrl)
    setShayPortfolioUrl(shayUrl)
    setPdfPassword(getConfigValue('pdf_password', ''))
  }, [getConfigValue])


  // Portfolio functions
  const handleDeletePortfolio = async (portfolioId: string) => {
    try {
      await deleteCustomPortfolio(parseInt(portfolioId))
      setShowDeleteConfirm(null)
    } catch (error) {
      console.error('Failed to delete portfolio:', error)
    }
  }

  // Sector functions
  const handleColorChange = (color: string) => {
    const colorOption = colorOptions.find(c => c.value === color)
    if (colorOption) {
      setSectorFormData(prev => ({
        ...prev,
        color: colorOption.value,
        bg_color: colorOption.bg,
        text_color: colorOption.text,
        border_color: colorOption.border
      }))
    }
  }

  const resetSectorForm = () => {
    setSectorFormData({
      key: '',
      label: '',
      icon: '',
      color: 'blue',
      bg_color: 'bg-blue-50 dark:bg-blue-900/20',
      text_color: 'text-blue-700 dark:text-blue-300',
      border_color: 'border-blue-200 dark:border-blue-700'
    })
  }

  const handleAddSector = () => {
    setIsAddSectorModalOpen(true)
    setEditingSectorId(null)
    resetSectorForm()
  }

  const handleEditSector = (sector: Sector) => {
    setEditingSectorId(sector.id)
    setSectorFormData({
      key: sector.key,
      label: sector.label,
      icon: sector.icon,
      color: sector.color,
      bg_color: sector.bg_color,
      text_color: sector.text_color,
      border_color: sector.border_color
    })
  }

  const handleSaveSector = async () => {
    if (!sectorFormData.key || !sectorFormData.label || !sectorFormData.icon) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      if (editingSectorId) {
        await updateSector(editingSectorId, sectorFormData)
      } else {
        await createSector(sectorFormData)
        setIsAddSectorModalOpen(false)
      }
      setEditingSectorId(null)
      resetSectorForm()
    } catch (error) {
      console.error('Error saving sector:', error)
    }
  }

  const handleCancelSector = () => {
    setIsAddSectorModalOpen(false)
    setEditingSectorId(null)
    resetSectorForm()
  }

  const handleDeleteSector = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this sector?')) {
      try {
        await deleteSector(id)
      } catch (error) {
        console.error('Error deleting sector:', error)
      }
    }
  }

  // Country functions
  const handleCountryColorChange = (color: string) => {
    const colorOption = colorOptions.find(c => c.value === color)
    if (colorOption) {
      setCountryFormData(prev => ({
        ...prev,
        color: colorOption.value,
        bg_color: colorOption.bg,
        text_color: colorOption.text,
        border_color: colorOption.border
      }))
    }
  }

  const resetCountryForm = () => {
    setCountryFormData({
      key: '',
      label: '',
      icon: '',
      color: 'blue',
      bg_color: 'bg-blue-50 dark:bg-blue-900/20',
      text_color: 'text-blue-700 dark:text-blue-300',
      border_color: 'border-blue-200 dark:border-blue-700'
    })
  }

  const handleAddCountry = () => {
    setIsAddCountryModalOpen(true)
    setEditingCountryId(null)
    resetCountryForm()
  }

  const handleEditCountry = (country: Country) => {
    setEditingCountryId(country.id)
    setCountryFormData({
      key: country.key,
      label: country.label,
      icon: country.icon,
      color: country.color,
      bg_color: country.bg_color,
      text_color: country.text_color,
      border_color: country.border_color
    })
  }

  const handleSaveCountry = async () => {
    if (!countryFormData.key || !countryFormData.label || !countryFormData.icon) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      if (editingCountryId) {
        await updateCountry(editingCountryId, countryFormData)
      } else {
        await createCountry(countryFormData)
        setIsAddCountryModalOpen(false)
      }
      setEditingCountryId(null)
      resetCountryForm()
    } catch (error) {
      console.error('Error saving country:', error)
    }
  }

  const handleCancelCountry = () => {
    setIsAddCountryModalOpen(false)
    setEditingCountryId(null)
    resetCountryForm()
  }

  const handleDeleteCountry = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this country?')) {
      try {
        await deleteCountry(id)
      } catch (error) {
        console.error('Error deleting country:', error)
      }
    }
  }

  // Broker functions
  const handleBrokerColorChange = (color: string) => {
    const colorOption = colorOptions.find(c => c.value === color)
    if (colorOption) {
      setBrokerFormData(prev => ({
        ...prev,
        color: colorOption.value,
        bg_color: colorOption.bg,
        text_color: colorOption.text,
        border_color: colorOption.border
      }))
    }
  }

  const resetBrokerForm = () => {
    setBrokerFormData({
      key: '',
      label: '',
      icon: '',
      color: 'blue',
      bg_color: 'bg-blue-50 dark:bg-blue-900/20',
      text_color: 'text-blue-700 dark:text-blue-300',
      border_color: 'border-blue-200 dark:border-blue-700'
    })
  }

  const handleAddBroker = () => {
    setIsAddBrokerModalOpen(true)
    setEditingBrokerId(null)
    resetBrokerForm()
  }

  const handleEditBroker = (broker: Broker) => {
    setEditingBrokerId(broker.id)
    setBrokerFormData({
      key: broker.key,
      label: broker.label,
      icon: broker.icon,
      color: broker.color,
      bg_color: broker.bg_color,
      text_color: broker.text_color,
      border_color: broker.border_color
    })
  }

  const handleSaveBroker = async () => {
    if (!brokerFormData.key || !brokerFormData.label || !brokerFormData.icon) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      if (editingBrokerId) {
        await updateBroker(editingBrokerId, brokerFormData)
      } else {
        await createBroker(brokerFormData)
        setIsAddBrokerModalOpen(false)
      }
      setEditingBrokerId(null)
      resetBrokerForm()
    } catch (error) {
      console.error('Error saving broker:', error)
    }
  }

  const handleCancelBroker = () => {
    setIsAddBrokerModalOpen(false)
    setEditingBrokerId(null)
    resetBrokerForm()
  }

  const handleDeleteBroker = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this broker?')) {
      try {
        await deleteBroker(id)
      } catch (error) {
        console.error('Error deleting broker:', error)
      }
    }
  }

  // Configuration functions
  const handleSaveGoogleSheetsUrl = async () => {
    if (!googleSheetsUrl) {
      toast.error('Please enter a valid Google Sheets URL')
      return
    }

    // Validate URL format
    if (!googleSheetsUrl.includes('docs.google.com/spreadsheets')) {
      toast.error('Please enter a valid Google Sheets URL')
      return
    }

    setIsSavingConfig(true)
    try {
      await updateConfig('google_sheets_url', googleSheetsUrl, 'Google Sheets URL for Alpha Picks data')
      toast.success('Google Sheets URL saved successfully')
    } catch (error) {
      console.error('Error saving Google Sheets URL:', error)
      toast.error('Failed to save Google Sheets URL')
    } finally {
      setIsSavingConfig(false)
    }
  }

  const handleSaveShayPortfolioUrl = async () => {
    if (!shayPortfolioUrl) {
      toast.error('Please enter a valid Google Sheets URL')
      return
    }

    // Validate URL format
    if (!shayPortfolioUrl.includes('docs.google.com/spreadsheets')) {
      toast.error('Please enter a valid Google Sheets URL')
      return
    }

    setIsSavingConfig(true)
    try {
      await updateConfig('shay_portfolio_url', shayPortfolioUrl, 'Google Sheets URL for Shay Portfolio')
      toast.success('Shay Portfolio URL saved successfully')
    } catch (error) {
      console.error('Error saving Shay Portfolio URL:', error)
      toast.error('Failed to save Shay Portfolio URL')
    } finally {
      setIsSavingConfig(false)
    }
  }

  const handleSavePdfPassword = async () => {
    setIsSavingPdfPassword(true)
    try {
      await updateConfig('pdf_password', pdfPassword, 'Password for protected PDF files')
      toast.success(pdfPassword ? 'PDF password saved' : 'PDF password cleared')
    } catch {
      toast.error('Failed to save PDF password')
    } finally {
      setIsSavingPdfPassword(false)
    }
  }

  const handleClearDatabase = async () => {
    try {
      await clearDatabase()
      setShowClearConfirm(false)
      // Refresh the page after clearing database
      window.location.reload()
    } catch (error) {
      console.error('Failed to clear database:', error)
    }
  }

  return (
    <motion.div
      variants={pageContainerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* Settings Header */}
      <motion.div variants={pageItemVariants} className="card p-6 ">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Settings
          </h2>
        </div>

        {/* Settings Layout with Sidebar */}
        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-48 flex-shrink-0">
            <nav className="space-y-2">
              <button
                onClick={() => setActiveSection('portfolios')}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                  activeSection === 'portfolios'
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <FolderOpen className="w-4 h-4" />
                Portfolios
              </button>
              <button
                onClick={() => setActiveSection('sectors')}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                  activeSection === 'sectors'
                    ? 'bg-brand-soft text-primary-700 dark:text-primary-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Building2 className="w-4 h-4" />
                Sectors
              </button>
              <button
                onClick={() => setActiveSection('countries')}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                  activeSection === 'countries'
                    ? 'bg-brand-soft text-primary-700 dark:text-primary-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Globe className="w-4 h-4" />
                Countries
              </button>
              <button
                onClick={() => setActiveSection('brokers')}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                  activeSection === 'brokers'
                    ? 'bg-brand-soft text-primary-700 dark:text-primary-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Landmark className="w-4 h-4" />
                Brokers
              </button>
              <button
                onClick={() => setActiveSection('configuration')}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                  activeSection === 'configuration'
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Sliders className="w-4 h-4" />
                Configuration
              </button>
              <button
                onClick={() => setActiveSection('bot')}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                  activeSection === 'bot'
                    ? 'bg-brand-soft text-primary-700 dark:text-primary-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Bot className="w-4 h-4" />
                Bot
              </button>
              <button
                onClick={() => setActiveSection('credit')}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                  activeSection === 'credit'
                    ? 'bg-brand-soft text-primary-700 dark:text-primary-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <CreditCard className="w-4 h-4" />
                Credit
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {activeSection === 'portfolios' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <FolderOpen className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Your Portfolios
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="secondary"
                      onClick={() => openCsvImportModal()}
                     
                     
                    >
                      <Upload className="w-4 h-4" />
                      Import CSV
                    </Button>
                    <Button size="sm"
                      onClick={() => openCustomPortfolioModal()}
                    >
                      <Plus className="w-4 h-4" />
                      <span className="font-semibold">Add Portfolio</span>
                    </Button>
                  </div>
                </div>

                {customPortfolios.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <PieChart className="w-6 h-6 text-gray-400" />
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      No portfolios yet
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Create your first portfolio to organize your investments
                    </p>
                    <Button
                      onClick={() => openCustomPortfolioModal()}
                     
                    >
                      <Plus className="w-4 h-4" />
                      Create Portfolio
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {customPortfolios.map((portfolioItem) => {
                      const isSelected = selectedPortfolioId === portfolioItem.id
                      
                      return (
                        <motion.div
                          key={portfolioItem.id}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                            isSelected
                              ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
                              : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="text-lg">{portfolioItem.icon}</div>
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <h4 className="font-medium text-gray-900 dark:text-white truncate">
                                {portfolioItem.name}
                              </h4>
                              {portfolioItem.is_default && (
                                <Badge variant="secondary" className="text-xs flex-shrink-0">
                                  Default
                                </Badge>
                              )}
                              <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto flex-shrink-0">
                                {portfolioItem.currency}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button variant="ghost" size="icon"
                              onClick={() => openCustomPortfolioModal(portfolioItem)}
                             
                              title="Edit portfolio"
                            >
                              <Edit3 className="w-3 h-3" />
                            </Button>
                            
                            {!portfolioItem.is_default && (
                              <Button variant="ghost" size="icon"
                                onClick={() => setShowDeleteConfirm(portfolioItem.id.toString())}
                               
                                title="Delete portfolio"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </motion.div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {activeSection === 'sectors' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-brand-soft rounded-lg flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Sector Management
                    </h3>
                  </div>
                  <Button
                    onClick={handleAddSector}
                  >
                    <Plus className="w-4 h-4" />
                    Add Sector
                  </Button>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Create and customize sector categories for your portfolio analysis
                </p>

                {sectors.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Building2 className="w-6 h-6 text-gray-400" />
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      No custom sectors yet
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Add your first sector to customize portfolio analysis
                    </p>
                    <Button
                      onClick={handleAddSector}
                    >
                      <Plus className="w-4 h-4" />
                      Add Sector
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {sectors.map((sector) => (
                      <div
                        key={sector.id}
                        className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        {editingSectorId === sector.id ? (
                          <div className="flex-1 grid grid-cols-4 gap-2 items-center">
                            <input
                              type="text"
                              value={sectorFormData.key}
                              onChange={(e) => setSectorFormData(prev => ({ ...prev, key: e.target.value }))}
                              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              placeholder="Key"
                            />
                            <input
                              type="text"
                              value={sectorFormData.label}
                              onChange={(e) => setSectorFormData(prev => ({ ...prev, label: e.target.value }))}
                              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              placeholder="Label"
                            />
                            <input
                              type="text"
                              value={sectorFormData.icon}
                              onChange={(e) => setSectorFormData(prev => ({ ...prev, icon: e.target.value }))}
                              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              placeholder="Icon"
                            />
                            <Select
                              value={sectorFormData.color}
                              onChange={handleColorChange}
                              options={colorOptions.map(c => ({ value: c.value, label: c.name, dot: c.dot }))}
                              size="sm"
                            />
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${sector.bg_color}`}>
                              <span className="text-sm">{sector.icon}</span>
                              <span className={`font-medium text-sm ${sector.text_color}`}>{sector.label}</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {sector.key}
                            </div>
                          </div>
                        )}

                        <div className="flex items-center gap-1">
                          {editingSectorId === sector.id ? (
                            <>
                              <Button variant="ghost" size="icon"
                                onClick={handleSaveSector}
                                title="Save"
                              >
                                <Save className="w-3 h-3" />
                              </Button>
                              <Button variant="ghost" size="icon"
                                onClick={handleCancelSector}
                                title="Cancel"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button variant="ghost" size="icon"
                                onClick={() => handleEditSector(sector)}
                               
                                title="Edit"
                               
                               
                              >
                                <Edit3 className="w-3 h-3" />
                              </Button>
                              <Button variant="ghost" size="icon"
                                onClick={() => handleDeleteSector(sector.id)}
                               
                                title="Delete"
                               
                               
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeSection === 'countries' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-brand-soft rounded-lg flex items-center justify-center">
                      <Globe className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Country Management
                    </h3>
                  </div>
                  <Button
                    onClick={handleAddCountry}
                  >
                    <Plus className="w-4 h-4" />
                    Add Country
                  </Button>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Create and customize country categories for your portfolio analysis
                </p>

                {countries.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Globe className="w-6 h-6 text-gray-400" />
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      No custom countries yet
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Add your first country to customize portfolio analysis
                    </p>
                    <Button
                      onClick={handleAddCountry}
                    >
                      <Plus className="w-4 h-4" />
                      Add Country
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {countries.map((country) => (
                      <div
                        key={country.id}
                        className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        {editingCountryId === country.id ? (
                          <div className="flex-1 grid grid-cols-4 gap-2 items-center">
                            <input
                              type="text"
                              value={countryFormData.key}
                              onChange={(e) => setCountryFormData(prev => ({ ...prev, key: e.target.value }))}
                              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              placeholder="Key"
                            />
                            <input
                              type="text"
                              value={countryFormData.label}
                              onChange={(e) => setCountryFormData(prev => ({ ...prev, label: e.target.value }))}
                              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              placeholder="Label"
                            />
                            <input
                              type="text"
                              value={countryFormData.icon}
                              onChange={(e) => setCountryFormData(prev => ({ ...prev, icon: e.target.value }))}
                              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              placeholder="Icon"
                            />
                            <Select
                              value={countryFormData.color}
                              onChange={handleCountryColorChange}
                              options={colorOptions.map(c => ({ value: c.value, label: c.name, dot: c.dot }))}
                              size="sm"
                            />
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${country.bg_color}`}>
                              <span className="text-sm">{country.icon}</span>
                              <span className={`font-medium text-sm ${country.text_color}`}>{country.label}</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {country.key}
                            </div>
                          </div>
                        )}

                        <div className="flex items-center gap-1">
                          {editingCountryId === country.id ? (
                            <>
                              <Button variant="ghost" size="icon"
                                onClick={handleSaveCountry}
                                title="Save"
                              >
                                <Save className="w-3 h-3" />
                              </Button>
                              <Button variant="ghost" size="icon"
                                onClick={handleCancelCountry}
                                title="Cancel"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button variant="ghost" size="icon"
                                onClick={() => handleEditCountry(country)}
                               
                                title="Edit"
                               
                               
                              >
                                <Edit3 className="w-3 h-3" />
                              </Button>
                              <Button variant="ghost" size="icon"
                                onClick={() => handleDeleteCountry(country.id)}
                               
                                title="Delete"
                               
                               
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeSection === 'brokers' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-brand-soft rounded-lg flex items-center justify-center">
                      <Landmark className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Broker Management
                    </h3>
                  </div>
                  <Button
                    onClick={handleAddBroker}
                  >
                    <Plus className="w-4 h-4" />
                    Add Broker
                  </Button>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Create and customize brokers (e.g. Dime, Webull, SCB FundClick) to tag your trading transactions
                </p>

                {brokers.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Landmark className="w-6 h-6 text-gray-400" />
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      No brokers yet
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Add your first broker to tag it on future trades
                    </p>
                    <Button
                      onClick={handleAddBroker}
                    >
                      <Plus className="w-4 h-4" />
                      Add Broker
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {brokers.map((broker) => (
                      <div
                        key={broker.id}
                        className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        {editingBrokerId === broker.id ? (
                          <div className="flex-1 grid grid-cols-4 gap-2 items-center">
                            <input
                              type="text"
                              value={brokerFormData.key}
                              onChange={(e) => setBrokerFormData(prev => ({ ...prev, key: e.target.value }))}
                              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              placeholder="Key"
                            />
                            <input
                              type="text"
                              value={brokerFormData.label}
                              onChange={(e) => setBrokerFormData(prev => ({ ...prev, label: e.target.value }))}
                              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              placeholder="Label"
                            />
                            <input
                              type="text"
                              value={brokerFormData.icon}
                              onChange={(e) => setBrokerFormData(prev => ({ ...prev, icon: e.target.value }))}
                              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              placeholder="Icon"
                            />
                            <Select
                              value={brokerFormData.color}
                              onChange={handleBrokerColorChange}
                              options={colorOptions.map(c => ({ value: c.value, label: c.name, dot: c.dot }))}
                              size="sm"
                            />
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${broker.bg_color}`}>
                              <BrokerLogo brokerKey={broker.key} fallbackIcon={broker.icon} size={18} />
                              <span className={`font-medium text-sm ${broker.text_color}`}>{broker.label}</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {broker.key}
                            </div>
                          </div>
                        )}

                        <div className="flex items-center gap-1">
                          {editingBrokerId === broker.id ? (
                            <>
                              <Button variant="ghost" size="icon"
                                onClick={handleSaveBroker}
                                title="Save"
                              >
                                <Save className="w-3 h-3" />
                              </Button>
                              <Button variant="ghost" size="icon"
                                onClick={handleCancelBroker}
                                title="Cancel"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button variant="ghost" size="icon"
                                onClick={() => handleEditBroker(broker)}
                               
                                title="Edit"
                               
                               
                              >
                                <Edit3 className="w-3 h-3" />
                              </Button>
                              <Button variant="ghost" size="icon"
                                onClick={() => handleDeleteBroker(broker.id)}
                               
                                title="Delete"
                               
                               
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeSection === 'bot' && <BotSettingsSection />}

            {activeSection === 'credit' && (
              <div>
                {/* Tab bar */}
                <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setCreditTab('profiles')}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      creditTab === 'profiles'
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <CreditCard className="w-4 h-4" />
                    Card Profiles
                  </button>
                  <button
                    onClick={() => setCreditTab('categories')}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      creditTab === 'categories'
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <Tag className="w-4 h-4" />
                    Categories
                  </button>
                  <button
                    onClick={() => setCreditTab('gmail')}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      creditTab === 'gmail'
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <Mail className="w-4 h-4" />
                    Gmail Sync
                    {gmailStatus?.connected && <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />}
                  </button>
                </div>

                {/* Card Profiles tab */}
                {creditTab === 'profiles' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-xs text-gray-500 dark:text-gray-400">กำหนดชื่อและลายหน้าบัตรตามเลข 4 ตัวท้าย</p>
                      <button
                        onClick={async () => {
                          setIsSavingCardProfiles(true)
                          try {
                            await updateConfig('credit_card_profiles', JSON.stringify(cardProfiles), 'Credit card display profiles')
                            toast.success('บันทึก Card Profiles แล้ว')
                          } catch {
                            toast.error('บันทึกไม่สำเร็จ')
                          } finally {
                            setIsSavingCardProfiles(false)
                          }
                        }}
                        disabled={isSavingCardProfiles}
                        className={buttonClass('primary', 'sm')}
                      >
                        <Save className="w-3.5 h-3.5" />
                        {isSavingCardProfiles ? 'Saving...' : 'Save'}
                      </button>
                    </div>

                    {!isCardProfilesLoaded ? (
                      <p className="text-sm text-gray-400 text-center py-8">กำลังโหลด...</p>
                    ) : (() => {
                      const seen = new Set(uniqueCards.map(c => c.last4))
                      const displayCards: UniqueCard[] = [
                        ...uniqueCards,
                        ...Object.keys(cardProfiles)
                          .filter(last4 => !seen.has(last4))
                          .map(last4 => ({ last4, bank: '', cardType: '' })),
                      ]
                      return displayCards.length === 0 ? (
                        <div className="text-center py-8 text-gray-400">
                          <CreditCard className="w-10 h-10 mx-auto mb-2 opacity-30" />
                          <p className="text-sm">ยังไม่มีข้อมูลบัตร — import statement PDF ก่อน</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {displayCards.map(c => {
                          const profile = cardProfiles[c.last4] ?? { name: '', image: null }
                          return (
                            <div key={c.last4} className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-3 border border-gray-200 dark:border-gray-600">
                              <div className="flex items-center gap-3">
                                {/* Card thumbnail */}
                                <div className="flex-shrink-0 flex flex-col items-center gap-0.5">
                                  {profile.image ? (
                                    <img src={profile.image} className="w-16 h-10 object-contain rounded-lg shadow" alt="" />
                                  ) : (
                                    <div className="w-16 h-10 rounded-lg bg-gray-500 flex items-center justify-center">
                                      <CreditCard className="w-4 h-4 text-white/60" />
                                    </div>
                                  )}
                                  <p className="text-[10px] text-gray-400 font-mono">···{c.last4}</p>
                                  <p className="text-[10px] text-gray-400">{c.bank}</p>
                                </div>

                                {/* Name input */}
                                <div className="flex-1 min-w-0">
                                  <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">ชื่อบัตร</label>
                                  <input
                                    type="text"
                                    value={profile.name}
                                    placeholder={c.cardType || `${c.bank} ···${c.last4}`}
                                    onChange={e => setCardProfiles(prev => ({
                                      ...prev,
                                      [c.last4]: { ...profile, name: e.target.value }
                                    }))}
                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  />
                                </div>

                                {/* Image picker trigger */}
                                <div className="flex-shrink-0 relative">
                                  <button
                                    onClick={() => setOpenPickerFor(openPickerFor === c.last4 ? null : c.last4)}
                                    className={buttonClass('secondary', 'sm')}
                                  >
                                    เปลี่ยนรูป
                                    <span className="text-gray-400">▼</span>
                                  </button>

                                  {openPickerFor === c.last4 && (
                                    <>
                                      <div className="fixed inset-0 z-10" onClick={() => setOpenPickerFor(null)} />
                                      <div className="absolute right-0 top-full mt-2 z-20 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-3 w-72">
                                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">เลือกลายหน้าบัตร</p>
                                        <div className="grid grid-cols-4 gap-2 max-h-72 overflow-y-auto">
                                          <button
                                            onClick={() => { setCardProfiles(prev => ({ ...prev, [c.last4]: { ...profile, image: null } })); setOpenPickerFor(null) }}
                                            className={`flex flex-col items-center gap-1 p-1.5 rounded-lg border-2 transition-all ${!profile.image ? 'border-primary-500 bg-brand-soft' : 'border-gray-200 dark:border-gray-600 hover:border-primary-300'}`}
                                          >
                                            <div className="w-full h-10 rounded bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                                              <CreditCard className="w-3 h-3 text-gray-400" />
                                            </div>
                                            <span className="text-[9px] text-gray-500 dark:text-gray-400 leading-tight">ไม่มีรูป</span>
                                          </button>
                                          {AVAILABLE_CARD_IMAGES.map(img => (
                                            <button
                                              key={img.path}
                                              onClick={() => { setCardProfiles(prev => ({ ...prev, [c.last4]: { ...profile, image: img.path } })); setOpenPickerFor(null) }}
                                              className={`flex flex-col items-center gap-1 p-1.5 rounded-lg border-2 transition-all ${profile.image === img.path ? 'border-primary-500 bg-brand-soft' : 'border-gray-200 dark:border-gray-600 hover:border-primary-300'}`}
                                            >
                                              <div className="w-full h-10 flex items-center justify-center overflow-hidden rounded">
                                                <img
                                                  src={img.path}
                                                  className="max-w-full max-h-full object-contain"
                                                  alt={img.label}
                                                />
                                              </div>
                                              <span className="text-[9px] text-gray-500 dark:text-gray-400 leading-tight text-center line-clamp-2">{img.label}</span>
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                      )
                    })()}
                  </div>
                )}

                {/* Categories tab */}
                {creditTab === 'categories' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Keywords สำหรับ categorize รายการบัตรเครดิตอัตโนมัติ (case-insensitive)
                      </p>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                        <button
                          onClick={() => { setCreditKeywords(DEFAULT_CREDIT_KEYWORDS); setIsCreditKwsLoaded(true) }}
                          className={buttonClass('secondary', 'sm')}
                        >
                          Reset
                        </button>
                        <button
                          onClick={async () => {
                            setIsSavingCreditKws(true)
                            try {
                              await updateConfig('credit_category_keywords', JSON.stringify(creditKeywords), 'Credit card category keywords')
                              toast.success('บันทึก keywords แล้ว')
                            } catch {
                              toast.error('บันทึกไม่สำเร็จ')
                            } finally {
                              setIsSavingCreditKws(false)
                            }
                          }}
                          disabled={isSavingCreditKws}
                          className={buttonClass('primary', 'sm')}
                        >
                          <Save className="w-3.5 h-3.5" />
                          {isSavingCreditKws ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>

                <div className="space-y-4">
                  {Object.entries(creditKeywords).map(([cat, kws]) => (
                    <div key={cat} className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center gap-2 mb-3">
                        <Tag className="w-4 h-4 text-primary-500" />
                        <span className="font-semibold text-gray-900 dark:text-white text-sm">{cat}</span>
                        <span className="text-xs text-gray-400">({kws.length} keywords)</span>
                        <button
                          onClick={() => {
                            if (!window.confirm(`ลบหมวดหมู่ "${cat}" และ keywords ทั้งหมด?`)) return
                            setCreditKeywords(prev => {
                              const next = { ...prev }
                              delete next[cat]
                              return next
                            })
                          }}
                          className="ml-auto p-1 text-gray-400 hover:text-red-500 transition-colors"
                          title="ลบหมวดหมู่"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {kws.map((kw) => (
                          <span
                            key={kw}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-full text-xs text-gray-700 dark:text-gray-300"
                          >
                            {kw}
                            <button
                              onClick={() => setCreditKeywords(prev => ({
                                ...prev,
                                [cat]: prev[cat].filter(k => k !== kw)
                              }))}
                              className="ml-0.5 text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newKwInputs[cat] || ''}
                          onChange={e => setNewKwInputs(prev => ({ ...prev, [cat]: e.target.value }))}
                          onKeyDown={e => {
                            if (e.key !== 'Enter') return
                            const val = (newKwInputs[cat] || '').trim().toUpperCase()
                            if (!val || kws.includes(val)) return
                            setCreditKeywords(prev => ({ ...prev, [cat]: [...prev[cat], val] }))
                            setNewKwInputs(prev => ({ ...prev, [cat]: '' }))
                          }}
                          placeholder="เพิ่ม keyword..."
                          className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                        <button
                          onClick={() => {
                            const val = (newKwInputs[cat] || '').trim().toUpperCase()
                            if (!val || kws.includes(val)) return
                            setCreditKeywords(prev => ({ ...prev, [cat]: [...prev[cat], val] }))
                            setNewKwInputs(prev => ({ ...prev, [cat]: '' }))
                          }}
                          className={buttonClass('secondary', 'sm')}
                        >
                          + Add
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Add new category */}
                  {showAddCat ? (
                    <div className="bg-primary-50 dark:bg-primary-900/10 rounded-xl p-4 border border-primary-200 dark:border-primary-800">
                      <p className="text-xs font-medium text-primary-700 dark:text-primary-400 mb-2">ชื่อหมวดหมู่ใหม่</p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newCatName}
                          onChange={e => setNewCatName(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Escape') { setShowAddCat(false); setNewCatName('') }
                            if (e.key !== 'Enter') return
                            const name = newCatName.trim()
                            if (!name) return
                            if (creditKeywords[name]) { toast.error('หมวดหมู่นี้มีอยู่แล้ว'); return }
                            setCreditKeywords(prev => ({ ...prev, [name]: [] }))
                            setNewCatName('')
                            setShowAddCat(false)
                          }}
                          autoFocus
                          placeholder="เช่น ประกัน, การศึกษา..."
                          className="flex-1 px-3 py-1.5 text-sm border border-primary-300 dark:border-primary-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                        <button
                          onClick={() => {
                            const name = newCatName.trim()
                            if (!name) return
                            if (creditKeywords[name]) { toast.error('หมวดหมู่นี้มีอยู่แล้ว'); return }
                            setCreditKeywords(prev => ({ ...prev, [name]: [] }))
                            setNewCatName('')
                            setShowAddCat(false)
                          }}
                          className={buttonClass('primary', 'sm')}
                        >
                          เพิ่ม
                        </button>
                        <button
                          onClick={() => { setShowAddCat(false); setNewCatName('') }}
                          className={buttonClass('secondary', 'sm')}
                        >
                          ยกเลิก
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowAddCat(true)}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-primary-200 dark:border-primary-800 text-primary-500 dark:text-primary-400 hover:border-primary-400 dark:hover:border-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors text-sm font-medium"
                    >
                      <Plus className="w-4 h-4" />
                      เพิ่มหมวดหมู่
                    </button>
                  )}
                </div>
                  </div>
                )}

                {/* Gmail Sync tab */}
                {creditTab === 'gmail' && (
                  <div className="space-y-5">
                    {/* Status card */}
                    <div className={`rounded-2xl p-5 border ${gmailStatus?.connected ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800' : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3">
                          {gmailStatus?.connected
                            ? <CheckCircle className="w-6 h-6 text-gain flex-shrink-0" />
                            : <Mail className="w-6 h-6 text-gray-400 flex-shrink-0" />}
                          <div>
                            <p className="font-medium text-gray-800 dark:text-gray-200 text-sm">
                              {gmailStatus?.connected ? 'เชื่อมต่อแล้ว' : 'ยังไม่ได้เชื่อมต่อ'}
                            </p>
                            {gmailStatus?.email && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{gmailStatus.email}</p>
                            )}
                            {gmailStatus?.last_sync && (
                              <p className="text-xs text-gray-400 dark:text-gray-400 mt-0.5">
                                sync ล่าสุด: {new Date(gmailStatus.last_sync).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' })}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          {gmailStatus?.connected ? (
                            <>
                              <button
                                onClick={async () => {
                                  setGmailSyncing(true)
                                  setGmailSyncResult(null)
                                  try {
                                    const res = await apiFetch('/api/gmail/sync', { method: 'POST' })
                                    const data = await res.json()
                                    if (!res.ok) { toast.error(data.error || 'Sync ไม่สำเร็จ'); return }
                                    setGmailSyncResult(data)
                                    setGmailStatus(prev => prev ? { ...prev, last_sync: new Date().toISOString() } : prev)
                                    toast.success(`Sync เสร็จ — บันทึก ${data.saved} statement`)
                                  } catch { toast.error('Sync ไม่สำเร็จ') }
                                  finally { setGmailSyncing(false) }
                                }}
                                disabled={gmailSyncing}
                                className={buttonClass('primary', 'sm')}
                              >
                                <RefreshCw className={`w-3.5 h-3.5 ${gmailSyncing ? 'animate-spin' : ''}`} />
                                {gmailSyncing ? 'กำลัง Sync...' : 'Sync ตอนนี้'}
                              </button>
                              <button
                                onClick={async () => {
                                  if (!window.confirm('ยกเลิกการเชื่อมต่อ Gmail?')) return
                                  await apiFetch('/api/gmail/disconnect', { method: 'DELETE' })
                                  setGmailStatus({ connected: false, email: '', last_sync: null })
                                  setGmailSyncResult(null)
                                  toast.success('ยกเลิกการเชื่อมต่อแล้ว')
                                }}
                                className={buttonClass('secondary', 'sm')}
                              >
                                ยกเลิกการเชื่อมต่อ
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={async () => {
                                try {
                                  const res = await apiFetch('/api/gmail/auth-url')
                                  if (!res.ok) {
                                    const err = await res.json()
                                    toast.error(err.error || 'ไม่สามารถเชื่อมต่อได้')
                                    return
                                  }
                                  const { url } = await res.json()
                                  window.location.href = url
                                } catch { toast.error('เกิดข้อผิดพลาด') }
                              }}
                              className={buttonClass('primary', 'md')}
                            >
                              <Mail className="w-4 h-4" />
                              เชื่อมต่อ Gmail
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Sync result */}
                    {gmailSyncResult && (
                      <div className="rounded-2xl p-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm">
                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">ผลการ Sync ล่าสุด</p>
                        <div className="grid grid-cols-3 gap-3 mb-3">
                          {[
                            { label: 'พบ email', value: gmailSyncResult.found, color: 'text-blue-600' },
                            { label: 'บันทึกแล้ว', value: gmailSyncResult.saved, color: 'text-gain' },
                            { label: 'ข้ามซ้ำ', value: gmailSyncResult.skipped, color: 'text-gray-500' },
                          ].map(s => (
                            <div key={s.label} className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
                            </div>
                          ))}
                        </div>
                        {gmailSyncResult.errors.length > 0 && (
                          <div className="space-y-1">
                            {gmailSyncResult.errors.map((e, i) => (
                              <div key={i} className="flex items-start gap-1.5 text-xs text-loss">
                                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                <span>{e}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* PDF Passwords */}
                    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <div className="px-5 py-4 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                        <Lock className="w-4 h-4 text-gray-500" />
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">PDF Password</p>
                        {savingPasswords && <span className="text-xs text-gray-400 ml-auto">กำลังบันทึก...</span>}
                        <button
                          onClick={() => setShowPasswordValues(v => !v)}
                          className="ml-auto text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title={showPasswordValues ? 'ซ่อน' : 'แสดง'}
                        >
                          {showPasswordValues ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <div className="px-5 py-3">
                        <p className="text-xs text-gray-400 mb-3">Password สำหรับ PDF ที่ lock อยู่ — ระบบจะลองทุกตัวอัตโนมัติตอน Sync</p>
                        {gmailPdfPasswords.length === 0 ? (
                          <p className="text-xs text-gray-400 italic">ยังไม่มี password</p>
                        ) : (
                          <div className="space-y-1 mb-2">
                            {gmailPdfPasswords.map((pwd, i) => (
                              <div key={i} className="flex items-center gap-2 py-1">
                                <span className="flex-1 text-xs font-mono text-gray-600 dark:text-gray-400">
                                  {showPasswordValues ? pwd : '•'.repeat(Math.min(pwd.length, 12))}
                                </span>
                                <button
                                  onClick={() => savePdfPasswords(gmailPdfPasswords.filter((_, j) => j !== i))}
                                  className="text-red-400 hover:text-red-600"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700/50 flex gap-2">
                        <input
                          type={showPasswordValues ? 'text' : 'password'}
                          value={newPasswordInput}
                          onChange={e => setNewPasswordInput(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && addPdfPassword()}
                          placeholder="เพิ่ม password"
                          className="flex-1 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <button
                          onClick={addPdfPassword}
                          disabled={!newPasswordInput.trim() || savingPasswords}
                          className={buttonClass('primary', 'md')}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Email Sender Patterns */}
                    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <div className="px-5 py-4 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                        <AtSign className="w-4 h-4 text-gray-500" />
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Email Sender ที่ค้นหา</p>
                        {savingSenders && <span className="text-xs text-gray-400 ml-auto">กำลังบันทึก...</span>}
                      </div>

                      {/* Preset senders */}
                      <div className="px-5 py-3 space-y-1">
                        <p className="text-xs text-gray-400 mb-2">ธนาคารที่รองรับ — tick เพื่อเปิด/ปิด</p>
                        {PRESET_SENDERS.map(({ pattern, label }) => {
                          const email = pattern.replace('from:', '')
                          const active = gmailSenders.includes(pattern)
                          return (
                            <label key={pattern} className="flex items-center gap-3 py-2 cursor-pointer group">
                              <input
                                type="checkbox"
                                checked={active}
                                onChange={() => toggleSender(pattern)}
                                className="w-4 h-4 rounded accent-blue-500 cursor-pointer"
                              />
                              <span className={`text-xs font-medium w-28 shrink-0 ${active ? 'text-gray-800 dark:text-gray-200' : 'text-gray-400 dark:text-gray-400'}`}>
                                {label}
                              </span>
                              <span className={`text-xs font-mono ${active ? 'text-gray-500 dark:text-gray-400' : 'text-gray-300 dark:text-gray-400'}`}>
                                {email}
                              </span>
                            </label>
                          )
                        })}
                      </div>

                      {/* Custom senders (not in preset list) */}
                      {gmailSenders.filter(s => !PRESET_SENDERS.some(p => p.pattern === s)).length > 0 && (
                        <div className="px-5 pb-3 border-t border-gray-100 dark:border-gray-700/50 pt-3">
                          <p className="text-xs text-gray-400 mb-2">Custom</p>
                          {gmailSenders
                            .filter(s => !PRESET_SENDERS.some(p => p.pattern === s))
                            .map(pattern => (
                              <div key={pattern} className="flex items-center gap-2 py-1.5">
                                <span className="flex-1 text-xs font-mono text-gray-600 dark:text-gray-400">
                                  {pattern.replace('from:', '')}
                                </span>
                                <button onClick={() => removeSender(pattern)} className="text-red-400 hover:text-red-600">
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}
                        </div>
                      )}

                      {/* Add custom sender */}
                      <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700/50 flex gap-2">
                        <input
                          type="text"
                          value={newSenderInput}
                          onChange={e => setNewSenderInput(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && addCustomSender()}
                          placeholder="เพิ่ม email เช่น noreply@mybank.com"
                          className="flex-1 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <button
                          onClick={addCustomSender}
                          disabled={!newSenderInput.trim() || savingSenders}
                          className={buttonClass('primary', 'md')}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* How it works */}
                    <div className="rounded-2xl p-5 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800">
                      <p className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3">วิธีการทำงาน</p>
                      <ol className="space-y-2 text-xs text-blue-700 dark:text-blue-400">
                        <li className="flex gap-2"><span className="font-bold">1.</span>ค้นหา email ที่มี PDF attachment จาก sender ที่เปิดไว้ข้างบน</li>
                        <li className="flex gap-2"><span className="font-bold">2.</span>ดาวน์โหลด PDF และ parse อัตโนมัติด้วย parser ที่มีอยู่</li>
                        <li className="flex gap-2"><span className="font-bold">3.</span>บันทึก statement ที่ยังไม่มีในระบบ (ข้ามรายการซ้ำโดยอัตโนมัติ)</li>
                        <li className="flex gap-2"><span className="font-bold">4.</span>แอปเข้าถึงแค่ email ที่มี PDF จากธนาคาร ไม่อ่าน email อื่น (scope: gmail.readonly)</li>
                      </ol>
                    </div>

                    {/* Setup requirements */}
                    {!gmailStatus?.connected && (
                      <div className="rounded-2xl p-5 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800">
                        <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2">ต้องตั้งค่าก่อนใช้งาน</p>
                        <p className="text-xs text-amber-700 dark:text-amber-400">
                          ต้องเพิ่ม <code className="bg-amber-100 dark:bg-amber-900/30 px-1 rounded">GOOGLE_CLIENT_ID</code> และ <code className="bg-amber-100 dark:bg-amber-900/30 px-1 rounded">GOOGLE_CLIENT_SECRET</code> ในไฟล์ <code className="bg-amber-100 dark:bg-amber-900/30 px-1 rounded">.env</code> ก่อน
                          แล้ว restart server
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeSection === 'configuration' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                      <Sliders className="w-4 h-4 text-gain" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      App Configuration
                    </h3>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Alpha Picks URL Configuration */}
                  <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                    <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <Database className="w-4 h-4" />
                      Alpha Picks Configuration
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Configure the Google Sheets URL for fetching Alpha Picks data. Make sure the sheet is publicly accessible.
                    </p>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Alpha Picks URL
                        </label>
                        <input
                          type="url"
                          value={googleSheetsUrl}
                          onChange={(e) => setGoogleSheetsUrl(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="https://docs.google.com/spreadsheets/d/..."
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Enter the full URL of your Alpha Picks Google Sheets document
                        </p>
                      </div>

                      <div className="flex items-center gap-3">
                        <Button
                          onClick={handleSaveGoogleSheetsUrl}
                          disabled={isSavingConfig}
                        >
                          <Save className="w-4 h-4" />
                          {isSavingConfig ? 'Saving...' : 'Save Configuration'}
                        </Button>

                        {googleSheetsUrl && (
                          <a
                            href={googleSheetsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-gain hover:underline"
                          >
                            Open in Google Sheets
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Shay Portfolio URL Configuration */}
                  <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                    <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <Database className="w-4 h-4" />
                      Shay Portfolio Configuration
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Configure the Google Sheets URL for Shay Portfolio data.
                    </p>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Shay Portfolio URL
                        </label>
                        <input
                          type="url"
                          value={shayPortfolioUrl}
                          onChange={(e) => setShayPortfolioUrl(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="https://docs.google.com/spreadsheets/d/..."
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Enter the full URL of Shay Portfolio Google Sheets document
                        </p>
                      </div>

                      <div className="flex items-center gap-3">
                        <Button
                          onClick={handleSaveShayPortfolioUrl}
                          disabled={isSavingConfig}
                        >
                          <Save className="w-4 h-4" />
                          {isSavingConfig ? 'Saving...' : 'Save Configuration'}
                        </Button>

                        {shayPortfolioUrl && (
                          <a
                            href={shayPortfolioUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-gain hover:underline"
                          >
                            Open in Google Sheets
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* PDF Password Configuration */}
                  <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                    <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      PDF Password
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      บันทึก password สำหรับไฟล์ PDF ที่มีการป้องกัน จะถูก pre-fill อัตโนมัติตอน Import PDF
                    </p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPdfPassword ? 'text' : 'password'}
                            value={pdfPassword}
                            onChange={(e) => setPdfPassword(e.target.value)}
                            className="w-full px-4 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="กรอก password ของ PDF"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPdfPassword(!showPdfPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          >
                            {showPdfPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {pdfPassword && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Password ถูกบันทึกแล้ว — จะ pre-fill ตอน Import PDF อัตโนมัติ
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <Button
                          onClick={handleSavePdfPassword}
                          disabled={isSavingPdfPassword}
                        >
                          <Save className="w-4 h-4" />
                          {isSavingPdfPassword ? 'Saving...' : 'Save Password'}
                        </Button>
                        {pdfPassword && (
                          <button
                            type="button"
                            onClick={() => { setPdfPassword(''); }}
                            className="text-sm text-loss hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Clear password
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Help Text */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                    <h5 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
                      How to set up Google Sheets:
                    </h5>
                    <ol className="text-sm text-blue-800 dark:text-blue-300 space-y-1 list-decimal list-inside">
                      <li>Create or open your Google Sheets document</li>
                      <li>Click "Share" and set sharing to "Anyone with the link can view"</li>
                      <li>Copy the full URL from your browser</li>
                      <li>Paste the URL in the field above and save</li>
                    </ol>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>


      {/* Add Sector Modal */}
      {isAddSectorModalOpen && (
        <div className="sheet fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add New Sector</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Key *
                </label>
                <input
                  type="text"
                  value={sectorFormData.key}
                  onChange={(e) => setSectorFormData(prev => ({ ...prev, key: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., technology"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Label *
                </label>
                <input
                  type="text"
                  value={sectorFormData.label}
                  onChange={(e) => setSectorFormData(prev => ({ ...prev, label: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Technology"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Icon *
                </label>
                <input
                  type="text"
                  value={sectorFormData.icon}
                  onChange={(e) => setSectorFormData(prev => ({ ...prev, icon: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., 💻"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Color
                </label>
                <Select
                  value={sectorFormData.color}
                  onChange={handleColorChange}
                  options={colorOptions.map(c => ({ value: c.value, label: c.name, dot: c.dot }))}
                />
              </div>

              <div className={`flex items-center gap-2 p-3 rounded-lg ${sectorFormData.bg_color}`}>
                <span className="text-lg">{sectorFormData.icon}</span>
                <span className={sectorFormData.text_color}>{sectorFormData.label}</span>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="secondary"
                onClick={handleCancelSector}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveSector}
              >
                <Save className="w-4 h-4" />
                Save
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Country Modal */}
      {isAddCountryModalOpen && (
        <div className="sheet fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add New Country</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Key *
                </label>
                <input
                  type="text"
                  value={countryFormData.key}
                  onChange={(e) => setCountryFormData(prev => ({ ...prev, key: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., usa"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Label *
                </label>
                <input
                  type="text"
                  value={countryFormData.label}
                  onChange={(e) => setCountryFormData(prev => ({ ...prev, label: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., United States"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Icon *
                </label>
                <input
                  type="text"
                  value={countryFormData.icon}
                  onChange={(e) => setCountryFormData(prev => ({ ...prev, icon: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., 🇺🇸"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Color
                </label>
                <Select
                  value={countryFormData.color}
                  onChange={handleCountryColorChange}
                  options={colorOptions.map(c => ({ value: c.value, label: c.name, dot: c.dot }))}
                />
              </div>

              <div className={`flex items-center gap-2 p-3 rounded-lg ${countryFormData.bg_color}`}>
                <span className="text-lg">{countryFormData.icon}</span>
                <span className={countryFormData.text_color}>{countryFormData.label}</span>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="secondary"
                onClick={handleCancelCountry}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveCountry}
              >
                <Save className="w-4 h-4" />
                Save
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Broker Modal */}
      {isAddBrokerModalOpen && (
        <div className="sheet fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add New Broker</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Key *
                </label>
                <input
                  type="text"
                  value={brokerFormData.key}
                  onChange={(e) => setBrokerFormData(prev => ({ ...prev, key: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., dime"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Label *
                </label>
                <input
                  type="text"
                  value={brokerFormData.label}
                  onChange={(e) => setBrokerFormData(prev => ({ ...prev, label: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Dime by KKP"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Icon *
                </label>
                <input
                  type="text"
                  value={brokerFormData.icon}
                  onChange={(e) => setBrokerFormData(prev => ({ ...prev, icon: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., 💎"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Color
                </label>
                <Select
                  value={brokerFormData.color}
                  onChange={handleBrokerColorChange}
                  options={colorOptions.map(c => ({ value: c.value, label: c.name, dot: c.dot }))}
                />
              </div>

              <div className={`flex items-center gap-2 p-3 rounded-lg ${brokerFormData.bg_color}`}>
                <BrokerLogo brokerKey={brokerFormData.key} fallbackIcon={brokerFormData.icon} size={22} />
                <span className={brokerFormData.text_color}>{brokerFormData.label}</span>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="secondary"
                onClick={handleCancelBroker}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveBroker}
              >
                <Save className="w-4 h-4" />
                Save
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Clear Database Confirmation Modal */}
      {showClearConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowClearConfirm(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-loss" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delete Database
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This action cannot be undone
                </p>
              </div>
            </div>
            
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 mb-6">
              <p className="text-red-800 dark:text-red-200 text-sm font-medium mb-2">
                This will permanently delete:
              </p>
              <ul className="text-red-700 dark:text-red-300 text-sm space-y-1">
                <li>• All portfolio holdings</li>
                <li>• All trading history</li>
                <li>• All price history</li>
                <li>• Custom portfolios (default ones will remain)</li>
                <li>• Custom sectors (default ones will remain)</li>
              </ul>
            </div>
            
            <div className="flex gap-3">
              <Button variant="secondary" className="flex-1"
                onClick={() => setShowClearConfirm(false)}
               
              >
                Cancel
              </Button>
              <Button variant="danger" className="flex-1"
                onClick={handleClearDatabase}
              >
                Delete Database
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowDeleteConfirm(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-loss" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delete Portfolio
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This action cannot be undone
                </p>
              </div>
            </div>
            
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Are you sure you want to delete this portfolio? All associated data will be permanently removed.
            </p>
            
            <div className="flex gap-3">
              <Button variant="secondary" className="flex-1"
                onClick={() => setShowDeleteConfirm(null)}
               
              >
                Cancel
              </Button>
              <Button variant="danger" className="flex-1"
                onClick={() => handleDeletePortfolio(showDeleteConfirm)}
               
              >
                Delete
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  )
}

export default PortfolioManagement